/**
 * The Apps tab's Data for one service: the datastores it uses (the Data tab's overview, `GET /data`) with their state,
 * size and records per table, collection or key space, as the server counts them. "Open Data" opens the full tab.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { getOverview } from '../../../services/DataService';
import { formatBytes } from '../../../services/SandboxService';
import { storesOf } from '../../../utils/serviceTabsModel';
import { Card, LoadState, OpenLink, useLoaded } from './common';

export default function DataTab({ projectId, service, onOpen = null }) {
  const state = useLoaded(() => getOverview(projectId), [projectId]);
  const stores = storesOf(state.value?.stores, service);
  return (
    <div className="apps-tab-body" data-testid="apps-tabpanel-data">
      {state.loading || state.error || stores.length === 0 ? (
        <Card testId="apps-data-none" title="Datastores"><LoadState empty="This service uses no datastore." state={state} /></Card>
      ) : (
        <div className="apps-infos">
          {stores.map((store) => (
            <Card key={store.id} testId={`apps-data-${store.id}`} title={<>{store.name || store.kind} <span className="apps-muted apps-small">{store.kind}</span></>}>
              <p className="apps-muted apps-small">
                {[store.status, store.sizeBytes ? formatBytes(store.sizeBytes) : null, store.readOnly ? 'read-only' : null].filter(Boolean).join(' · ')}
              </p>
              {store.objects.length === 0 ? <p className="apps-muted">Nothing in it yet.</p> : (
                <ul className="apps-rows">
                  {store.objects.map((object) => (
                    <li key={`${object.schema || ''}.${object.name}`}>
                      <span className="apps-row-name">{object.name}</span>
                      <span className="apps-muted">{object.count === undefined || object.count === null ? object.kind : `${object.count.toLocaleString()} ${object.kind === 'keys' ? 'keys' : 'records'}`}</span>
                    </li>
                  ))}
                  {store.more > 0 && <li className="apps-muted">and {store.more} more</li>}
                </ul>
              )}
            </Card>
          ))}
        </div>
      )}
      <OpenLink label="Open Data" onOpen={onOpen} testId="apps-open-data" />
    </div>
  );
}

DataTab.propTypes = { onOpen: PropTypes.func, projectId: PropTypes.string, service: PropTypes.string.isRequired };
